import MoreVertRounded from "@mui/icons-material/MoreVertRounded";
import { IconButton, Stack, Typography } from "@mui/material";
import { DocumentPreviewDialog } from "@shared";
import { format } from "date-fns";

import { DocumentGroup, DocumentResponse } from "@/api";
import { FileActions } from "@/common/components/layout/components/dropzone/DropZone.types";
import { DocumentSelectCheckbox } from "@/common/components/layout/components/dropzone/components/document-select-checkbox/DocumentSelectCheckbox";
import { useDocumentRowActivation } from "@/common/components/layout/components/dropzone/dnd/hooks/useDocumentRowActivation";
import { RenameInput } from "@/common/components/rename-input/RenameInput";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { formatContentType, formatFileSize } from "@/common/helper/fileDownloadHelper";
import { useTranslation } from "@/translations";

import { DocumentWidgetMenu } from "./components/widget-menu/DocumentWidgetMenu";
import { useDocumentWidget } from "./hooks/useDocumentWidget";

interface Props {
  file: DocumentResponse;
  selected: boolean;
  onToggleSelect: (fileId: string) => void;
  fileActions: FileActions;
  sourceZone: DocumentGroup;
}

export function DocumentWidget({ file, selected, onToggleSelect, fileActions, sourceZone }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const {
    menuAnchor,
    setMenuAnchor,
    rename,
    preview,
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
    handleDownload,
    handleDeleteClick,
    handleRenameClick,
    handleCreateCaseNoteClick,
    handleConvertToPdfClick,
  } = useDocumentWidget({ file, sourceZone, fileActions });

  const rowActivation = useDocumentRowActivation({
    onActivate: preview.handlePreview,
    disabled: rename.isEditing || isDragging || preview.isPreviewOpen,
  });

  return (
    <Stack
      ref={(node: HTMLElement | null) => {
        setNodeRef(node);
        setActivatorNodeRef(node);
      }}
      {...(rename.isEditing ? {} : listeners)}
      {...(rename.isEditing ? {} : attributes)}
      {...rowActivation}
      aria-label={rename.isEditing ? undefined : t("dropzone.dnd.openPreview", { fileName: file.fileName })}
      direction="row"
      sx={{
        p: 0,
        minHeight: 0,
        alignItems: "center",
        gap: 0.5,
        opacity: isDragging ? 0.4 : 1,
        cursor: rename.isEditing ? "default" : "grab",
        "&:active": { cursor: rename.isEditing ? "default" : "grabbing" },
      }}
    >
      <DocumentSelectCheckbox
        fileName={file.fileName}
        checked={selected}
        onToggle={() => onToggleSelect(file.documentId)}
      />
      <Stack sx={{ overflow: "hidden", flex: 1 }}>
        {rename.isEditing ? (
          <RenameInput
            editValue={rename.editValue}
            setEditValue={rename.setEditValue}
            inputRef={rename.inputRef}
            extension={rename.extension}
            confirmRename={rename.confirmRename}
            handleKeyDown={rename.handleKeyDown}
          />
        ) : (
          <Typography variant="body2" noWrap>
            {file.fileName} ({formatContentType(file.contentType)}, {formatFileSize(file.fileSizeBytes)})
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" noWrap>
          {format(file.assignedAtUtc, "Pp", { locale: locales[language] })}
        </Typography>
      </Stack>
      <IconButton
        aria-label={t("dropzone.fileList.openFileActions", { fileName: file.fileName })}
        aria-haspopup="menu"
        disabled={fileActions.isBusy}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        <MoreVertRounded />
      </IconButton>
      <DocumentWidgetMenu
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onRename={handleRenameClick}
        onDownload={handleDownload}
        onDelete={handleDeleteClick}
        onCreateCaseNote={handleCreateCaseNoteClick}
        onConvertToPdf={handleConvertToPdfClick}
        onPreview={preview.handlePreview}
      />
      {preview.isPreviewOpen && (
        <DocumentPreviewDialog
          blob={preview.previewBlob}
          isLoading={preview.isPreviewLoading}
          fileName={file.fileName}
          uploadedAt={file.assignedAtUtc}
          onClose={preview.closePreview}
        />
      )}
    </Stack>
  );
}
